import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ExtendedCandidate {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  assessmentTitle: string;
  assessment_id: string;
  status: 'invited' | 'called' | 'in_progress' | 'completed';
  completedDate?: string | null;
  score?: number | null;
  aiRating?: number | null;
  potentialPlagiarism?: boolean;
  skill_ratings?: Array<{ skill: string; rating: number }> | null;
  ai_summary?: string | null;
  ats_candidate_id?: string | null;
  ats_application_id?: string | null;
  fit_status?: 'good_fit' | 'maybe' | 'not_fit' | null;
}

export interface CandidateAssessment {
  id: string;
  candidate_id: string;
  assessment_id: string;
  audio_url?: string;
  transcript?: any;
  created_at: string;
  updated_at: string;
}

export function useSelectCandidate() {
  const [selectedCandidate, setSelectedCandidate] = useState<ExtendedCandidate | null>(null);
  const [candidateAssessment, setCandidateAssessment] = useState<CandidateAssessment | null>(null);

  const handleCandidateClick = async (candidateId: string) => {
    try {
      // Fetch candidate details
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select('*, assessments(title)')
        .eq('id', candidateId)
        .single();

      if (candidateError) {
        console.error('Error fetching candidate:', candidateError);
        toast.error('Unable to load candidate details');
        return;
      }

      if (!candidateData) {
        console.error('No candidate data found');
        toast.error('Candidate not found');
        return;
      }

      console.log('Fetched candidate data:', {
        id: candidateData.id,
        assessment_id: candidateData.assessment_id,
        status: candidateData.status
      });

      // Format candidate data
      const candidate: ExtendedCandidate = {
        id: candidateData.id,
        name: candidateData.name,
        email: candidateData.email,
        phone: candidateData.phone,
        assessmentTitle: candidateData.assessments?.title || '',
        assessment_id: candidateData.assessment_id,
        status: candidateData.status,
        completedDate: candidateData.completed_date,
        score: candidateData.score,
        aiRating: candidateData.ai_rating,
        potentialPlagiarism: candidateData.potential_plagiarism,
        skill_ratings: candidateData.skill_ratings,
        ai_summary: candidateData.ai_summary,
        ats_candidate_id: candidateData.ats_candidate_id,
        ats_application_id: candidateData.ats_application_id,
        fit_status: candidateData.fit_status
      };

      setSelectedCandidate(candidate);

      // Only fetch assessment data if the candidate has completed the assessment
      if (candidateData.status === 'completed') {
        try {
          const { data: assessmentData, error: assessmentError } = await supabase
            .from('candidate_assessments')
            .select('*')
            .eq('candidate_id', candidateId)
            .eq('assessment_id', candidateData.assessment_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (assessmentError) {
            console.error('Error fetching assessment:', assessmentError);
            // Don't show error toast here since we already have candidate data
            return;
          }

          if (assessmentData) {
            // Get the public URL for the audio file if it exists
            if (assessmentData.audio_url) {
              const audioPath = `${candidateData.assessment_id}/${candidateId}/conversation.mp3`;
              const { data: { publicUrl } } = supabase.storage
                .from('assessment-recordings')
                .getPublicUrl(audioPath);

              // Add cache-busting parameter to ensure we get the latest file
              const cacheBuster = `?v=${Date.now()}`;
              assessmentData.audio_url = publicUrl + cacheBuster;
            }

            setCandidateAssessment(assessmentData);
          }
        } catch (assessmentError) {
          console.error('Error processing assessment data:', assessmentError);
          // Don't show error toast here since we already have candidate data
        }
      } else {
        console.log('Candidate has not completed assessment, skipping assessment data fetch');
        setCandidateAssessment(null);
      }

    } catch (error) {
      console.error('Error in handleCandidateClick:', error);
      toast.error('Failed to load candidate details');
      setSelectedCandidate(null);
      setCandidateAssessment(null);
    }
  };

  const handleCloseCandidate = () => {
    setSelectedCandidate(null);
    setCandidateAssessment(null);
  };

  return {
    selectedCandidate,
    candidateAssessment,
    handleCandidateClick,
    handleCloseCandidate
  };
} 